import { Controller, useFormContext } from 'react-hook-form';

import { InputField } from '@nesso/ui/primitives/input-field';
import { MaskedInputField } from '@nesso/ui/primitives/masked-input-field';
import { SelectField } from '@nesso/ui/primitives/select-field';

import { US_STATE_OPTIONS } from '../../constants/us-state-options';
import { FieldReveal } from './field-reveal';

type AddressFormValues = {
    address: {
        street1: string;
        street2: string;
        city: string;
        state: string;
        zip: string;
    };
};

type Props = {
    maxLength: number;
    autoFocus?: boolean;
    disabled?: boolean;
};

export function UsAddressFields({ maxLength, autoFocus, disabled = false }: Props) {
    const {
        control,
        formState: { isSubmitting, defaultValues },
    } = useFormContext<AddressFormValues>();
    const isDisabled = isSubmitting || disabled;
    // Read from `defaultValues`, not the live value, so emptying the field does not collapse it.
    const hasSavedStreet2 = !!defaultValues?.address?.street2;

    return (
        <>
            <Controller
                control={control}
                name="address.street1"
                render={({ field, fieldState }) => (
                    <InputField
                        {...field}
                        id={field.name}
                        label="Street Address"
                        autoFocus={autoFocus}
                        disabled={isDisabled}
                        maxLength={maxLength}
                        error={fieldState.error?.message ?? fieldState.invalid}
                    />
                )}
            />
            <Controller
                control={control}
                name="address.street2"
                render={({ field, fieldState }) => (
                    <FieldReveal
                        label="Apt, Suite, Floor"
                        disabled={isDisabled}
                        hasSavedValue={hasSavedStreet2}
                    >
                        {(revealedByClick) => (
                            <InputField
                                {...field}
                                id={field.name}
                                label="Apt, Suite, Floor"
                                autoFocus={revealedByClick}
                                disabled={isDisabled}
                                maxLength={maxLength}
                                error={fieldState.error?.message ?? fieldState.invalid}
                            />
                        )}
                    </FieldReveal>
                )}
            />
            <Controller
                control={control}
                name="address.city"
                render={({ field, fieldState }) => (
                    <InputField
                        {...field}
                        id={field.name}
                        label="City"
                        disabled={isDisabled}
                        maxLength={maxLength}
                        error={fieldState.error?.message ?? fieldState.invalid}
                    />
                )}
            />
            <div className="flex flex-col gap-6 md:flex-row">
                <Controller
                    control={control}
                    name="address.state"
                    render={({ field, fieldState }) => (
                        <SelectField
                            {...field}
                            id={field.name}
                            label="State"
                            options={US_STATE_OPTIONS}
                            clearable
                            disabled={isDisabled}
                            error={fieldState.error?.message ?? fieldState.invalid}
                            fieldClassName="md:max-w-55.5"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="address.zip"
                    render={({ field, fieldState }) => (
                        <MaskedInputField
                            {...field}
                            id={field.name}
                            label="ZIP"
                            inputMode="numeric"
                            mask="00000"
                            disabled={isDisabled}
                            error={fieldState.error?.message ?? fieldState.invalid}
                            className="max-w-28.75 md:max-w-18.75"
                        />
                    )}
                />
            </div>
        </>
    );
}
